import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button, ModalBody, ModalFooter, ModalHeader, Modal } from 'common/components';
import { ButtonVariant } from 'common/enums';
import { FieldError } from 'common/models';
import { ACTION_ITEMS } from 'common/config';
import { addValidationError, removeValidationError } from 'common/utils/error';
import { validateMaxLength } from 'common/utils/validator';

import IndicatorSourceForm from 'modules/configuration/components/indicator-source-form/IndicatorSourceForm';
import { IndicatorSourceDTO } from 'modules/configuration/models/configuration.model';
import { IndicatorSourceField } from 'modules/configuration/enums/configuration.enum';
import {
    SOURCE_NAME_EMPTY_ERROR,
    SOURCE_NAME_MAX_LENGTH,
    SOURCE_NAME_MAX_LENGTH_ERROR,
    WEB_ADDRESS_EMPTY_ERROR,
    WEB_ADDRESS_MAX_LENGTH,
    WEB_ADDRESS_MAX_LENGTH_ERROR
} from 'modules/configuration/config';
import { configurationActions } from 'modules/configuration/redux';

import './IndicatorSourceAddEdit.scss';

type IndicatorSourceAddEditProps = {
    data: IndicatorSourceDTO | undefined;
    onClose: () => void;
    isVisible: boolean;
};

const IndicatorSourceAddEdit: FC<IndicatorSourceAddEditProps> = ({ data, onClose, isVisible }) => {
    const [sourceName, setSourceName] = useState<string>(data ? data.sourceName : '');
    const [webAddress, setWebAddress] = useState<string>(data ? data.webAddress : '');
    const [fieldErrors, setFieldErrors] = useState<Array<FieldError>>([]);

    const dispatch = useDispatch();

    const handleUpdate = () => {
        let errors = fieldErrors;
        if (!sourceName.trim()) {
            errors = addValidationError(
                errors,
                IndicatorSourceField.SourceName,
                SOURCE_NAME_EMPTY_ERROR
            );
        }
        if (!webAddress.trim()) {
            errors = addValidationError(
                errors,
                IndicatorSourceField.WebAddress,
                WEB_ADDRESS_EMPTY_ERROR
            );
        }
        if (errors.length === 0) {
            if (data) {
                const updatedData: IndicatorSourceDTO = {
                    sourceId: data.sourceId,
                    sourceName: sourceName,
                    webAddress: webAddress,
                    createdDtm: data.createdDtm
                };
                dispatch(configurationActions.updateIndicatorSource(updatedData));
            } else {
                const updatedData: IndicatorSourceDTO = {
                    sourceId: '',
                    sourceName: sourceName,
                    webAddress: webAddress,
                    createdDtm: ''
                };
                dispatch(configurationActions.addIndicatorSource(updatedData));
            }
        }
        setFieldErrors(errors);
    };

    const handleSourceNameChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, IndicatorSourceField.SourceName);
        if (event.target.value.trim().length === 0) {
            errors = addValidationError(
                errors,
                IndicatorSourceField.SourceName,
                SOURCE_NAME_EMPTY_ERROR
            );
        }
        if (validateMaxLength(event.target.value, SOURCE_NAME_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                IndicatorSourceField.SourceName,
                SOURCE_NAME_MAX_LENGTH_ERROR
            );
        }
        setSourceName(event.target.value);
        setFieldErrors(errors);
    };

    const handleWebAddressChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, IndicatorSourceField.WebAddress);
        if (event.target.value.trim().length === 0) {
            errors = addValidationError(
                errors,
                IndicatorSourceField.WebAddress,
                WEB_ADDRESS_EMPTY_ERROR
            );
        }
        if (validateMaxLength(event.target.value, WEB_ADDRESS_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                IndicatorSourceField.WebAddress,
                WEB_ADDRESS_MAX_LENGTH_ERROR
            );
        }
        setWebAddress(event.target.value);
        setFieldErrors(errors);
    };

    const headerElement = (
        <header className="indicator-source-edit-header-name-wrapper">
            {data ? 'Edit - Indicator Source' : 'Add new - Indicator Source'}
        </header>
    );

    return (
        <Modal show={isVisible} style={{ width: '51vw' }} onClose={onClose}>
            <ModalHeader className="indicator-source-edit-header">{headerElement}</ModalHeader>
            <ModalBody className="indicator-source-edit-body">
                <IndicatorSourceForm
                    fieldErrors={fieldErrors}
                    sourceName={sourceName}
                    handleSourceNameChange={handleSourceNameChange}
                    webAddress={webAddress}
                    handleWebAddressChange={handleWebAddressChange}
                />
            </ModalBody>
            <ModalFooter className="indicator-source-edit-footer">
                <Button
                    variant={ButtonVariant.Primary}
                    onClick={handleUpdate}
                    disabled={fieldErrors.length > 0}>
                    {ACTION_ITEMS.SAVE_BUTTON}
                </Button>
                <Button variant={ButtonVariant.Secondary} onClick={onClose}>
                    {ACTION_ITEMS.CANCEL_BUTTON}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default IndicatorSourceAddEdit;
